import { Bot, InputFile } from "grammy";
import type { ChannelAdapter, ChannelRuntime, InboundMessage, OutboundMessage } from "@mewbleh/nori-plugin-sdk";

export class TelegramChannel implements ChannelAdapter {
  readonly id = "telegram";
  readonly capabilities = { media: true, reactions: true, threads: true, groups: true, login: "token" as const };

  async start(runtime: ChannelRuntime): Promise<{ stop(): Promise<void> }> {
    const token = String(runtime.config.token ?? "");
    if (!token) throw new Error("Telegram bot token is required");
    const bot = new Bot(token);
    bot.on("message", async (context) => {
      const message = context.message;
      const chatKind = message.chat.type === "private" ? "dm" : "group";
      const text = message.text ?? message.caption ?? "";
      const media: InboundMessage["media"] = [];
      const file = message.document ?? message.photo?.at(-1) ?? message.video ?? message.audio ?? message.voice;
      if (file) {
        try {
          const resolved = await context.api.getFile(file.file_id);
          if (resolved.file_path) {
            media.push({
              url: `https://api.telegram.org/file/bot${token}/${resolved.file_path}`,
              mediaType: "application/octet-stream",
              ...(message.document?.file_name ? { name: message.document.file_name } : {}),
            });
          }
        } catch (error) {
          runtime.log("warn", "Unable to resolve Telegram attachment", error);
        }
      }
      await runtime.onMessage({
        id: String(message.message_id),
        conversation: {
          channel: this.id,
          accountId: runtime.accountId,
          id: String(message.chat.id),
          kind: chatKind,
          ...(message.message_thread_id ? { threadId: String(message.message_thread_id) } : {}),
        },
        sender: {
          id: String(message.from?.id ?? "unknown"),
          ...(message.from?.username || message.from?.first_name
            ? { displayName: message.from.username ?? message.from.first_name }
            : {}),
          ...(message.from?.is_bot !== undefined ? { isBot: message.from.is_bot } : {}),
        },
        text,
        ...(media.length ? { media } : {}),
        ...(message.reply_to_message ? { replyToId: String(message.reply_to_message.message_id) } : {}),
        mentioned: Boolean(bot.botInfo?.username && text.toLowerCase().includes(`@${bot.botInfo.username.toLowerCase()}`)),
        receivedAt: message.date * 1000,
      });
    });
    bot.catch((error) => runtime.log("error", "Telegram update failed", { message: error.message }));
    await bot.init();
    void bot.start({ onStart: () => runtime.log("info", "Telegram channel connected", { username: bot.botInfo.username }) });
    const abort = () => bot.stop();
    runtime.signal.addEventListener("abort", abort, { once: true });
    return {
      stop: async () => {
        runtime.signal.removeEventListener("abort", abort);
        await bot.stop();
      },
    };
  }

  async send(message: OutboundMessage, runtime: Omit<ChannelRuntime, "onMessage">): Promise<{ messageId: string }> {
    const token = String(runtime.config.token ?? "");
    const bot = new Bot(token);
    const chatId = message.conversation.id;
    if (message.media?.length) {
      const media = message.media[0]!;
      const file = media.data ? new InputFile(media.data, media.name) : media.url!;
      const sent = await bot.api.sendDocument(chatId, file, {
        ...(message.text ? { caption: message.text } : {}),
        ...(message.replyToId ? { reply_parameters: { message_id: Number(message.replyToId) } } : {}),
        ...(message.conversation.threadId ? { message_thread_id: Number(message.conversation.threadId) } : {}),
      });
      return { messageId: String(sent.message_id) };
    }
    const sent = await bot.api.sendMessage(chatId, message.text, {
      ...(message.replyToId ? { reply_parameters: { message_id: Number(message.replyToId) } } : {}),
      ...(message.conversation.threadId ? { message_thread_id: Number(message.conversation.threadId) } : {}),
    });
    return { messageId: String(sent.message_id) };
  }

  async probe(config: Record<string, unknown>): Promise<{ ok: boolean; message?: string }> {
    try {
      const me = await new Bot(String(config.token ?? "")).api.getMe();
      return { ok: true, message: `@${me.username}` };
    } catch (error) {
      return { ok: false, message: (error as Error).message };
    }
  }
}
